export const CLAUSEBOOK_PICK_INSTRUCTIONS = `Choose at most one clausebook candidate for the scenario.

None is a normal outcome. Select a candidate only when its language actually fits the scenario; the presence of candidates is not an instruction to use one. For example, if the scenario concerns assignment rights and every candidate concerns insurance, decline by returning {"selected":null}. Do not choose the least-bad candidate.

Candidates are identified by their one-based position in the list. When you select one, return its position and a concise reason it fits. A selection requires a reason. When none fits, return {"selected":null}; declining requires no explanation, though one is accepted if you have it.`;

const pickedClauseSchema = {
  title: 'picked_clause',
  type: 'object',
  additionalProperties: false,
  properties: {
    selected: { type: 'integer', minimum: 1 },
    reason: { type: 'string', pattern: '\\S' },
  },
  required: ['selected', 'reason'],
};

// `reason` is PERMITTED here and absent from `required`: that, not its
// exclusion, is the asymmetry. Declining must cost nothing to get right, so a
// model that declines and explains anyway has still declined. Forbidding the
// field would make the safe outcome the one with a shape to get wrong — the
// opposite of what design section 5 asks for — and the model is called with
// `strict => false` (services_controller.rb:2210 and friends), so the schema
// only guides it and this validator is what actually decides.
const declinedClauseSchema = {
  title: 'declined_clause',
  type: 'object',
  additionalProperties: false,
  properties: {
    selected: { type: 'null' },
    reason: { type: ['string', 'null'] },
  },
  required: ['selected'],
};

// Separate branches make declining a first-class successful response without
// making `reason` optional for the riskier outcome: choosing language to use.
//
// A Ruby mirror of this cannot be a transliteration. The Responses API's
// json_schema format does not support `oneOf` and rejects a non-object root —
// DOCUMENT_PATCH_SHAPES hit the same wall and is kept in `anyOf` under an
// object root for that reason (services_controller.rb:292).
export const CLAUSEBOOK_PICK_SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  oneOf: [pickedClauseSchema, declinedClauseSchema],
};

function invalid(reason) {
  return { valid: false, reason };
}

/**
 * Validate a model pick against the exact candidate list it saw. A decline
 * stays a decline; callers must not replace it with a fallback candidate
 * merely because the retrieval returned results.
 */
export function validateClausebookPick(pick, candidates) {
  if (!pick || typeof pick !== 'object' || Array.isArray(pick)) {
    return invalid('pick must be an object');
  }

  const properties = Object.keys(pick);
  if (!Object.prototype.hasOwnProperty.call(pick, 'selected')) {
    return invalid('selected is required');
  }

  if (pick.selected === null) {
    if (
      properties.some(property => !['selected', 'reason'].includes(property))
    ) {
      return invalid('a decline may contain only selected and reason');
    }
    if (
      pick.reason !== undefined &&
      pick.reason !== null &&
      typeof pick.reason !== 'string'
    ) {
      return invalid('reason must be a string when a decline gives one');
    }
    const declineReason =
      typeof pick.reason === 'string' ? pick.reason.trim() : '';
    // Normalised away when blank so callers can test the decline itself rather
    // than having to distinguish an absent reason from an empty one.
    return {
      valid: true,
      value: declineReason
        ? { selected: null, reason: declineReason }
        : { selected: null },
    };
  }

  if (!Number.isInteger(pick.selected) || pick.selected < 1) {
    return invalid('selected must be null or a positive integer');
  }
  if (!Array.isArray(candidates) || pick.selected > candidates.length) {
    return invalid('selected must name a candidate in the presented list');
  }
  if (typeof pick.reason !== 'string' || pick.reason.trim() === '') {
    return invalid('reason is required when a candidate is selected');
  }
  if (properties.some(property => !['selected', 'reason'].includes(property))) {
    return invalid('a pick may contain only selected and reason');
  }

  return {
    valid: true,
    value: { selected: pick.selected, reason: pick.reason.trim() },
  };
}
